/**
 * Phase 6B — Premium Winhouse Learning Centre (Victorian Selective Entry).
 *
 * Adds: Ask AI Coach, smart reminders, progress dashboard, related topics,
 * mastery + completion indicators, Discussion Hub polish (unread, pinned,
 * role badges, search, file uploads, moderation), continue-where-you-left-off,
 * recommended next topic.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, ArrowLeft, Sparkles, Trophy, FileDown, MessageCircle,
  Send, CheckCircle2, Clock, Flame, Bot, Pin, PinOff, Search, Paperclip,
  TrendingUp, Star, Target, Lightbulb, ChevronRight, BookOpen,
} from 'lucide-react';
import QuestionVisual from '../../components/shared/QuestionVisual';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const DIFF_COLORS = {
  Easy: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  Medium: 'bg-amber-100 text-amber-700 border-amber-200',
  Hard: 'bg-rose-100 text-rose-700 border-rose-200',
};

const LearningCentrePage = () => {
  const navigate = useNavigate();
  const [view, setView] = useState('home'); // home | topic | mock | hub | progress | resources
  const [activeTopic, setActiveTopic] = useState(null);
  const [activeMock, setActiveMock] = useState(null);
  const [topics, setTopics] = useState({ mathematics: [], english: [] });
  const [mocks, setMocks] = useState([]);
  const [hub, setHub] = useState(null);
  const [revision, setRevision] = useState(null);
  const [progressMap, setProgressMap] = useState({});
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [tm, te, mk, rc, rm, pr, rs] = await Promise.all([
        ax.get('/api/learning/tracks/mathematics/topics'),
        ax.get('/api/learning/tracks/english/topics'),
        ax.get('/api/learning/mocks'),
        ax.get('/api/me/student/learning/revision-centre'),
        ax.get('/api/learning/rooms/me'),
        ax.get('/api/me/student/learning/progress'),
        ax.get('/api/learning/resources'),
      ]);
      setTopics({ mathematics: tm.data?.items || [], english: te.data?.items || [] });
      setMocks(mk.data?.items || []);
      setRevision(rc.data);
      setResources(rs.data?.items || []);
      const map = {};
      // Build a map of topic_slug -> progress for badges
      (pr.data?.weakest_topics || []).forEach((w) => { map[w.topic_slug] = w; });
      // Also include quiz_history latest score per topic
      (pr.data?.quiz_history || []).forEach((a) => {
        if (!map[a.reference_slug]) map[a.reference_slug] = { topic_slug: a.reference_slug, best_score_pct: a.score_pct };
        else if ((a.score_pct || 0) > (map[a.reference_slug].best_score_pct || 0)) map[a.reference_slug].best_score_pct = a.score_pct;
      });
      setProgressMap(map);
      const room = rm.data?.room;
      if (room) {
        const msgs = await ax.get(`/api/learning/rooms/${room.room_id}/messages`);
        setHub({ room, messages: msgs.data?.messages || [], pinned: msgs.data?.pinned || [], unread: msgs.data?.unread_count || 0 });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load Learning Centre');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openTopic = async (slug) => {
    try {
      const r = await ax.get(`/api/learning/topics/${slug}`);
      setActiveTopic(r.data);
      setView('topic');
    } catch (e) { toast.error('Could not open topic'); }
  };
  const openMock = async (slug) => {
    try {
      const r = await ax.get(`/api/learning/mocks/${slug}`);
      setActiveMock(r.data);
      setView('mock');
    } catch (e) { toast.error('Could not open mock'); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FAF9F6]"><Loader2 className="h-7 w-7 animate-spin text-red-700" /></div>;

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="learning-centre">
      <header className="bg-black text-white px-6 py-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <button onClick={() => navigate('/student/dashboard')} className="text-[10px] uppercase tracking-[3px] text-[#D3D86F] inline-flex items-center gap-1 hover:underline" data-testid="learning-back">
            <ArrowLeft className="h-3 w-3" /> Student Portal
          </button>
          <h1 className="text-xl font-bold mt-1">Winhouse Learning Centre · Victorian Selective Entry</h1>
        </div>
        <div className="flex gap-2 flex-wrap text-xs">
          <NavBtn active={view==='home'} onClick={() => setView('home')} testid="nav-home">Revision</NavBtn>
          <NavBtn active={view==='progress'} onClick={() => setView('progress')} testid="nav-progress">My Progress</NavBtn>
          <NavBtn active={view==='resources'} onClick={() => setView('resources')} testid="nav-resources">Resources</NavBtn>
          <NavBtn active={false} onClick={() => navigate('/student/exams')} testid="nav-exams">Exam Centre</NavBtn>
          <NavBtn active={view==='hub'} onClick={() => setView('hub')} testid="nav-hub">
            Discussion Hub{hub?.unread > 0 && <span className="ml-1 bg-rose-500 text-white text-[10px] px-1.5 rounded-full">{hub.unread}</span>}
          </NavBtn>
          <a href={`${BACKEND_URL}/api/me/student/timetable.pdf`} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 inline-flex items-center gap-1" data-testid="nav-pdf"><FileDown className="h-3 w-3"/> Timetable PDF</a>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {view === 'home' && (
          <>
            {revision && <RevisionCentreTile data={revision} onOpenTopic={openTopic} onOpenMock={openMock} />}
            {['mathematics','english'].map(tr => (
              <section key={tr} className="bg-white rounded-2xl border border-gray-200 p-5" data-testid={`track-${tr}`}>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-base font-bold text-gray-900 capitalize">{tr}</h2>
                  <Badge>{topics[tr].filter(t=>t.status==='published').length}/{topics[tr].length} published</Badge>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                  {topics[tr].map(t => (
                    <TopicCard key={t.slug} topic={t} progress={progressMap[t.slug]} onClick={() => openTopic(t.slug)} />
                  ))}
                </div>
              </section>
            ))}
            <section className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="mocks-section">
              <h2 className="text-base font-bold text-gray-900 mb-3 flex items-center gap-2"><Trophy className="h-4 w-4 text-amber-600" /> Mock Exams</h2>
              <div className="grid sm:grid-cols-3 gap-3">
                {mocks.map(m => (
                  <button key={m.slug} onClick={() => openMock(m.slug)} className="text-left border border-gray-200 rounded-xl p-3 hover:border-amber-300 hover:bg-amber-50/40 transition" data-testid={`mock-${m.slug}`}>
                    <div className="text-sm font-bold text-gray-900">{m.title}</div>
                    <div className="text-[10px] uppercase tracking-wider text-gray-500 mt-0.5">{m.track} · {m.duration_minutes} min · {m.question_count} questions</div>
                    <div className="text-xs text-gray-600 mt-1.5 line-clamp-2">{m.description}</div>
                  </button>
                ))}
              </div>
            </section>
          </>
        )}
        {view === 'topic' && activeTopic && <TopicRunner topic={activeTopic} onDone={() => { setView('home'); load(); }} onOpenTopic={openTopic} />}
        {view === 'mock' && activeMock && <MockRunner mock={activeMock} onDone={() => { setView('home'); load(); }} />}
        {view === 'hub' && hub && <DiscussionHub hub={hub} onReload={load} />}
        {view === 'progress' && <ProgressDashboard onOpenTopic={openTopic} />}
        {view === 'resources' && <ResourcesLibrary resources={resources} />}
      </main>
    </div>
  );
};

const NavBtn = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} className={`px-3 py-1.5 rounded-full ${active?'bg-[#D3D86F] text-black':'bg-white/10 hover:bg-white/20'}`} data-testid={testid}>{children}</button>
);

const Badge = ({ children }) => <span className="text-[10px] uppercase tracking-wider font-bold text-gray-600 bg-gray-100 px-2 py-0.5 rounded">{children}</span>;

const TopicCard = ({ topic, progress, onClick }) => {
  const score = progress?.best_score_pct;
  const mastered = score && score >= (topic.mastery_threshold || 80);
  const completed = score && score >= 70;
  const diffClass = DIFF_COLORS[topic.difficulty] || 'bg-gray-100 text-gray-700 border-gray-200';
  return (
    <button onClick={onClick} disabled={topic.status!=='published'}
      className={`text-left border rounded-xl p-3 transition relative ${topic.status==='published'?'border-gray-200 hover:border-red-300 hover:bg-red-50/40':'border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed'}`}
      data-testid={`topic-${topic.slug}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm font-bold text-gray-900 flex-1">{topic.title}</div>
        {mastered && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-500" data-testid={`mastered-${topic.slug}`} />}
        {!mastered && completed && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" data-testid={`completed-${topic.slug}`} />}
      </div>
      <div className="flex flex-wrap items-center gap-1 mt-1">
        <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border ${diffClass}`}>{topic.difficulty || 'Medium'}</span>
        {topic.estimated_minutes && <span className="text-[9px] uppercase tracking-wider text-gray-500 inline-flex items-center gap-0.5"><Clock className="h-2.5 w-2.5"/>{topic.estimated_minutes}m</span>}
        {topic.status!=='published' && <span className="text-[9px] uppercase tracking-wider text-gray-400">Draft</span>}
      </div>
      <div className="text-xs text-gray-600 mt-1.5 line-clamp-2">{topic.summary || 'Content coming soon.'}</div>
      {typeof score === 'number' && score > 0 && (
        <div className="mt-2">
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className={`h-full ${mastered?'bg-amber-500':completed?'bg-emerald-500':'bg-red-500'}`} style={{width: `${Math.min(100, score)}%`}}/>
          </div>
          <div className="text-[10px] text-gray-500 mt-0.5">Best: {score}%</div>
        </div>
      )}
    </button>
  );
};

const RevisionCentreTile = ({ data, onOpenTopic, onOpenMock }) => (
  <div className="bg-gradient-to-br from-red-700 to-red-900 text-white rounded-2xl p-5" data-testid="revision-centre-tile">
    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[2px] text-[#D3D86F] font-bold"><Sparkles className="h-3 w-3"/> Revision Centre</div>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
      <Stat label="Topics completed" value={`${data.totals.topics_completed}/${data.totals.topics_total}`} />
      <Stat label="Mastered" value={data.totals.topics_mastered || 0} icon={<Star className="h-3 w-3"/>} />
      <Stat label="Mocks taken" value={data.totals.mocks_taken} />
      <Stat label="Streak" value={`${data.totals.streak_days} day${data.totals.streak_days===1?'':'s'}`} icon={<Flame className="h-3 w-3"/>} />
    </div>
    {data.continue_topic && (
      <button onClick={() => onOpenTopic(data.continue_topic.slug)} className="mt-3 w-full text-left bg-white/10 hover:bg-white/15 rounded-xl px-3 py-2.5 transition" data-testid="continue-where-left-off">
        <div className="text-[10px] uppercase tracking-[2px] text-[#D3D86F] font-bold flex items-center gap-1"><ChevronRight className="h-3 w-3"/> Continue where you left off</div>
        <div className="text-sm font-bold mt-0.5">{data.continue_topic.title}</div>
        <div className="text-[11px] text-white/80">{data.continue_topic.difficulty} · {data.continue_topic.estimated_minutes || 25} min</div>
      </button>
    )}
    {data.reminders?.length > 0 && (
      <div className="mt-3 text-xs space-y-1.5" data-testid="reminders-list">
        {data.reminders.map((r,i) => (
          <div key={i} className={`rounded-lg px-3 py-2 flex items-start gap-2 ${r.severity==='high'?'bg-rose-500/30 border border-rose-300/40':'bg-white/10'}`}>
            <Lightbulb className="h-3.5 w-3.5 mt-0.5 text-[#D3D86F] flex-shrink-0"/>
            <div className="flex-1">
              <strong>{r.title}</strong> — {r.body}
            </div>
            {r.topic_slug && <button onClick={() => onOpenTopic(r.topic_slug)} className="text-[10px] uppercase tracking-wider bg-white text-red-800 px-2 py-1 rounded-full font-bold whitespace-nowrap">Revise</button>}
            {r.mock_slug && <button onClick={() => onOpenMock(r.mock_slug)} className="text-[10px] uppercase tracking-wider bg-[#D3D86F] text-black px-2 py-1 rounded-full font-bold whitespace-nowrap">Sit Mock</button>}
          </div>
        ))}
      </div>
    )}
  </div>
);

const Stat = ({label,value,icon}) => (
  <div className="bg-white/10 rounded-xl p-2.5">
    <div className="text-[10px] uppercase tracking-wider text-white/70 font-bold flex items-center gap-1">{icon}{label}</div>
    <div className="text-lg font-bold mt-0.5">{value}</div>
  </div>
);

const TopicRunner = ({ topic, onDone, onOpenTopic }) => {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(null);
  const [busy, setBusy] = useState(false);
  const [coachOpen, setCoachOpen] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await ax.post('/api/learning/quiz/submit', {
        kind: 'topic_quiz', reference_slug: topic.topic.slug, answers,
      });
      setSubmitted(r.data);
      toast.success(`Score: ${r.data.score_pct ?? 'n/a'}%`);
    } catch (e) { toast.error('Submit failed'); }
    finally { setBusy(false); }
  };

  const diffClass = DIFF_COLORS[topic.topic.difficulty] || 'bg-gray-100 text-gray-700 border-gray-200';

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="topic-runner">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button onClick={onDone} className="text-[10px] uppercase tracking-[2px] text-red-700 inline-flex items-center gap-1 hover:underline"><ArrowLeft className="h-3 w-3"/> Back</button>
        <button onClick={() => setCoachOpen(o => !o)} className="text-xs bg-black text-white px-3 py-1.5 rounded-full inline-flex items-center gap-1 hover:bg-gray-800" data-testid="open-ai-coach">
          <Bot className="h-3.5 w-3.5"/> {coachOpen ? 'Close AI Coach' : 'Ask AI Coach'}
        </button>
      </div>
      <h2 className="text-xl font-bold text-gray-900 mt-3">{topic.topic.title}</h2>
      <div className="flex flex-wrap items-center gap-2 mt-1">
        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded border ${diffClass}`}>{topic.topic.difficulty || 'Medium'}</span>
        {topic.topic.estimated_minutes && <span className="text-[10px] uppercase tracking-wider text-gray-500 inline-flex items-center gap-0.5"><Clock className="h-2.5 w-2.5"/> {topic.topic.estimated_minutes} min</span>}
        <span className="text-[10px] uppercase tracking-wider text-gray-500">{topic.total_questions} questions</span>
      </div>
      <p className="text-sm text-gray-700 mt-3">{topic.topic.summary}</p>
      <div className="mt-4 bg-gray-50 rounded-xl p-4 text-sm whitespace-pre-wrap" data-testid="lesson-notes">{topic.topic.lesson_notes}</div>
      {topic.topic.illustration && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-3 font-mono text-xs whitespace-pre-wrap" data-testid="topic-illustration">
          <div className="text-[10px] uppercase tracking-wider text-amber-800 font-bold mb-1">Illustration</div>
          {topic.topic.illustration}
        </div>
      )}
      {topic.topic.worked_examples?.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="worked-examples">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Worked examples</div>
          {topic.topic.worked_examples.map((w,i) => (
            <div key={i} className="border-l-2 border-amber-400 pl-3 text-sm">
              <div className="font-medium">{w.prompt}</div>
              {w.step_by_step?.length > 0 && (
                <ol className="text-xs text-gray-700 mt-1 list-decimal pl-5 space-y-0.5" data-testid={`steps-${i}`}>
                  {w.step_by_step.map((s,si) => <li key={si}>{s}</li>)}
                </ol>
              )}
              <div className="text-gray-600 mt-1"><strong>Answer:</strong> {w.solution}</div>
            </div>
          ))}
        </div>
      )}
      {topic.topic.common_mistakes?.length > 0 && (
        <div className="mt-3 bg-rose-50 border border-rose-200 rounded-xl p-3" data-testid="common-mistakes">
          <div className="text-[10px] uppercase tracking-wider text-rose-700 font-bold mb-1">Common mistakes to avoid</div>
          <ul className="text-xs text-gray-800 list-disc pl-5 space-y-0.5">
            {topic.topic.common_mistakes.map((m,i) => <li key={i}>{m}</li>)}
          </ul>
        </div>
      )}
      {topic.topic.revision_summary?.length > 0 && (
        <div className="mt-3 bg-emerald-50 border border-emerald-200 rounded-xl p-3" data-testid="revision-summary">
          <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold mb-1">Revision summary</div>
          <ul className="text-xs text-gray-800 list-disc pl-5 space-y-0.5">
            {topic.topic.revision_summary.map((m,i) => <li key={i}>{m}</li>)}
          </ul>
        </div>
      )}
      {coachOpen && <AICoachPanel topicSlug={topic.topic.slug} />}
      {topic.questions.length > 0 && !submitted && (
        <div className="mt-5 space-y-4">
          <div className="text-sm font-bold text-gray-900">Practice quiz</div>
          {topic.questions.map((q,i) => (
            <div key={q.id} className="border border-gray-200 rounded-xl p-3" data-testid={`q-${q.id}`}>
              <QuestionVisual visual_type={q.visual_type} visual_payload={q.visual_payload}
                visual_caption={q.visual_caption} visual_alt_text={q.visual_alt_text} />
              <div className="text-sm font-medium text-gray-900">{i+1}. {q.prompt}</div>
              {q.kind === 'mcq' && q.choices && (
                <div className="mt-2 grid sm:grid-cols-2 gap-1.5">
                  {q.choices.map((c,ci) => (
                    <label key={ci} className={`text-xs border rounded-lg px-2.5 py-1.5 cursor-pointer transition ${answers[q.id]===ci?'border-red-500 bg-red-50':'border-gray-200 hover:bg-gray-50'}`}>
                      <input type="radio" name={q.id} className="hidden" onChange={() => setAnswers(p=>({...p,[q.id]:ci}))} />
                      {String.fromCharCode(65+ci)}. {c}
                    </label>
                  ))}
                </div>
              )}
              {q.kind === 'writing' && (
                <textarea rows={4} placeholder="Type your response…" className="mt-2 w-full text-sm border rounded-lg p-2" onChange={e=>setAnswers(p=>({...p,[q.id]:e.target.value}))} />
              )}
            </div>
          ))}
          <button onClick={submit} disabled={busy} className="mt-2 bg-red-700 hover:bg-red-800 text-white px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-50" data-testid="quiz-submit">
            {busy?<Loader2 className="h-3 w-3 animate-spin"/>:<Send className="h-3 w-3"/>} Submit answers
          </button>
        </div>
      )}
      {submitted && <ResultPanel result={submitted} masteryThreshold={topic.topic.mastery_threshold || 80} />}
      {topic.related_topics?.length > 0 && (
        <div className="mt-6 border-t pt-4" data-testid="related-topics">
          <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mb-2">Related revision topics</div>
          <div className="flex flex-wrap gap-2">
            {topic.related_topics.map(rt => (
              <button key={rt.slug} onClick={() => onOpenTopic(rt.slug)} className="text-xs border border-gray-200 rounded-full px-3 py-1.5 hover:border-red-300 hover:bg-red-50/40 inline-flex items-center gap-1" data-testid={`related-${rt.slug}`}>
                <BookOpen className="h-3 w-3"/> {rt.title}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const AICoachPanel = ({ topicSlug }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    ax.get(`/api/learning/coach/history?topic_slug=${topicSlug}`)
      .then(r => setMessages(r.data?.messages || []))
      .catch(() => {});
  }, [topicSlug]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    if (!input.trim()) return;
    setBusy(true);
    const myMsg = { role: 'student', content: input.trim() };
    setMessages(m => [...m, myMsg]);
    setInput('');
    try {
      const r = await ax.post('/api/learning/coach/ask', { topic_slug: topicSlug, message: myMsg.content });
      setMessages(r.data?.messages || []);
    } catch (e) { toast.error('AI Coach is busy — try again'); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-4 border border-black/20 bg-gradient-to-br from-gray-50 to-white rounded-2xl p-4" data-testid="ai-coach-panel">
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[2px] text-black font-bold"><Bot className="h-3 w-3"/> Winhouse AI Coach</div>
      <p className="text-[11px] text-gray-500 mt-0.5">I won’t just give you answers — I’ll guide you to think through it step by step.</p>
      <div ref={scrollRef} className="mt-3 max-h-72 overflow-y-auto space-y-2 pr-1" data-testid="coach-messages">
        {messages.length === 0 && <div className="text-xs italic text-gray-400">Ask me about a worked example, a tricky concept, or a question you got wrong.</div>}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role==='student'?'justify-end':'justify-start'}`}>
            <div className={`max-w-[85%] text-sm rounded-2xl px-3 py-2 whitespace-pre-wrap ${m.role==='student'?'bg-red-700 text-white':'bg-white border border-gray-200 text-gray-900'}`}>
              {m.content}
            </div>
          </div>
        ))}
        {busy && <div className="flex justify-start"><div className="bg-white border border-gray-200 rounded-2xl px-3 py-2 text-xs text-gray-500 flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin"/> Thinking…</div></div>}
      </div>
      <div className="mt-3 flex gap-2">
        <input type="text" value={input} onChange={e=>setInput(e.target.value)} onKeyDown={e=>{if(e.key==='Enter') send();}}
          placeholder="Ask the coach for a hint…" className="flex-1 text-sm border rounded-full px-4 py-2" data-testid="coach-input"/>
        <button onClick={send} disabled={busy||!input.trim()} className="bg-black hover:bg-gray-800 text-white px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1 disabled:opacity-40" data-testid="coach-send">
          <Send className="h-3 w-3"/> Ask
        </button>
      </div>
    </div>
  );
};

const MockRunner = ({ mock, onDone }) => {
  const [answers, setAnswers] = useState({});
  const [flagged, setFlagged] = useState({});
  const [submitted, setSubmitted] = useState(null);
  const [busy, setBusy] = useState(false);
  const [currentQ, setCurrentQ] = useState(0);
  const [timeLeft, setTimeLeft] = useState(mock.mock.duration_minutes * 60);
  const [reviewMode, setReviewMode] = useState(false);

  useEffect(() => {
    if (submitted) return;
    const id = setInterval(() => setTimeLeft(t => {
      if (t <= 1) { clearInterval(id); return 0; }
      return t - 1;
    }), 1000);
    return () => clearInterval(id);
  }, [submitted]);

  useEffect(() => {
    if (timeLeft === 0 && !submitted) {
      toast.warning("Time up — auto-submitting your mock");
      submit();
    }
  }, [timeLeft]);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await ax.post('/api/learning/quiz/submit', {
        kind: 'mock', reference_slug: mock.mock.slug, answers,
        duration_seconds: mock.mock.duration_minutes * 60 - timeLeft,
      });
      setSubmitted(r.data);
      toast.success(`Mock complete: ${r.data.score_pct ?? 'n/a'}%`);
    } catch (e) { toast.error('Mock submit failed'); }
    finally { setBusy(false); }
  };

  const toggleFlag = (qid) => setFlagged(f => ({ ...f, [qid]: !f[qid] }));
  const min = Math.floor(timeLeft / 60);
  const sec = timeLeft % 60;
  const q = mock.questions[currentQ];
  const answeredCount = Object.keys(answers).length;
  const flaggedCount = Object.values(flagged).filter(Boolean).length;
  const timeWarning = timeLeft <= 60;

  if (submitted) {
    return (
      <div className="bg-white rounded-2xl border border-amber-200 p-5" data-testid="mock-result">
        <button onClick={onDone} className="text-[10px] uppercase tracking-[2px] text-amber-700 inline-flex items-center gap-1 hover:underline mb-3"><ArrowLeft className="h-3 w-3"/> Back</button>
        <ResultPanel result={submitted} />
        {!reviewMode && submitted.per_question?.length > 0 && (
          <button onClick={() => setReviewMode(true)} className="mt-4 text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-full font-bold uppercase tracking-wider" data-testid="mock-review-answers">Review answers</button>
        )}
        {reviewMode && (
          <div className="mt-3 space-y-2" data-testid="mock-review-list">
            {submitted.per_question.map((pq, i) => (
              <div key={i} className={`border rounded-lg p-2 text-xs ${pq.correct===false?'border-rose-200 bg-rose-50':'border-emerald-200 bg-emerald-50'}`}>
                <div className="font-bold">Q{i+1}: {pq.prompt}</div>
                {pq.explanation && <div className="text-gray-700 mt-1">{pq.explanation}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-amber-200 p-5" data-testid="mock-runner">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <button onClick={onDone} className="text-[10px] uppercase tracking-[2px] text-amber-700 inline-flex items-center gap-1 hover:underline"><ArrowLeft className="h-3 w-3"/> Exit (no save)</button>
        <div className={`font-mono text-sm font-bold px-3 py-1 rounded-full ${timeWarning?'bg-rose-100 text-rose-700 animate-pulse':'bg-amber-100 text-amber-700'}`} data-testid="mock-timer">
          <Clock className="h-3 w-3 inline mr-1"/> {String(min).padStart(2,'0')}:{String(sec).padStart(2,'0')}
        </div>
      </div>
      <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2"><Trophy className="h-4 w-4 text-amber-600"/> {mock.mock.title}</h2>
      <div className="text-[10px] uppercase tracking-wider text-gray-500 mt-1">{mock.mock.duration_minutes} min · {mock.questions.length} questions · {answeredCount} answered · {flaggedCount} flagged</div>

      {/* Question navigator */}
      <div className="mt-3 flex flex-wrap gap-1" data-testid="mock-navigator">
        {mock.questions.map((qq, i) => (
          <button key={qq.id} onClick={()=>setCurrentQ(i)}
            className={`w-7 h-7 rounded text-[10px] font-bold ${i===currentQ?'bg-amber-600 text-white':answers[qq.id]!==undefined?'bg-emerald-100 text-emerald-800':'bg-gray-100 text-gray-600 hover:bg-gray-200'} ${flagged[qq.id]?'ring-2 ring-rose-400':''}`}
            data-testid={`mock-nav-${i}`}>{i+1}</button>
        ))}
      </div>

      {/* Current question */}
      <div className="mt-4 border border-gray-200 rounded-xl p-3" data-testid={`mock-q-${q.id}`}>
        <QuestionVisual visual_type={q.visual_type} visual_payload={q.visual_payload}
          visual_caption={q.visual_caption} visual_alt_text={q.visual_alt_text} />
        <div className="flex items-start justify-between gap-2">
          <div className="text-sm font-medium flex-1">{currentQ+1}. {q.prompt}</div>
          <button onClick={()=>toggleFlag(q.id)} className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${flagged[q.id]?'bg-rose-100 text-rose-700':'bg-gray-100 text-gray-600'}`} data-testid="mock-flag">{flagged[q.id]?'Unflag':'Flag'}</button>
        </div>
        {q.choices && (
          <div className="mt-2 grid sm:grid-cols-2 gap-1.5">
            {q.choices.map((c,ci) => (
              <label key={ci} className={`text-xs border rounded-lg px-2.5 py-1.5 cursor-pointer ${answers[q.id]===ci?'border-amber-500 bg-amber-50':'border-gray-200 hover:bg-gray-50'}`}>
                <input type="radio" name={q.id} className="hidden" onChange={()=>setAnswers(p=>({...p,[q.id]:ci}))}/>
                {String.fromCharCode(65+ci)}. {c}
              </label>
            ))}
          </div>
        )}
        {q.kind === 'writing' && (
          <textarea rows={5} placeholder="Type your response…" className="mt-2 w-full text-sm border rounded-lg p-2" onChange={e=>setAnswers(p=>({...p,[q.id]:e.target.value}))} />
        )}
      </div>

      {/* Bottom nav */}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-2">
          <button onClick={()=>setCurrentQ(Math.max(0, currentQ-1))} disabled={currentQ===0} className="text-xs bg-gray-100 hover:bg-gray-200 disabled:opacity-40 px-3 py-1.5 rounded-full" data-testid="mock-prev">Previous</button>
          <button onClick={()=>setCurrentQ(Math.min(mock.questions.length-1, currentQ+1))} disabled={currentQ===mock.questions.length-1} className="text-xs bg-gray-100 hover:bg-gray-200 disabled:opacity-40 px-3 py-1.5 rounded-full" data-testid="mock-next">Next</button>
        </div>
        <button onClick={submit} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-white px-5 py-2 rounded-full text-xs font-bold uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-50" data-testid="mock-submit">
          {busy?<Loader2 className="h-3 w-3 animate-spin"/>:<Trophy className="h-3 w-3"/>} Submit mock
        </button>
      </div>
    </div>
  );
};

const ResultPanel = ({ result, masteryThreshold = 80 }) => {
  const mastered = result.score_pct != null && result.score_pct >= masteryThreshold;
  return (
    <div className={`mt-5 rounded-xl p-4 border ${mastered?'bg-amber-50 border-amber-200':'bg-emerald-50 border-emerald-200'}`} data-testid="quiz-result">
      <div className="text-sm font-bold flex items-center gap-2">
        {mastered ? <Star className="h-4 w-4 text-amber-600 fill-amber-500"/> : <CheckCircle2 className="h-4 w-4 text-emerald-700"/>}
        <span className={mastered?'text-amber-900':'text-emerald-900'}>{result.correct}/{result.total} correct · {result.score_pct ?? 'n/a'}% {mastered && '· Mastered!'}</span>
      </div>
      {result.ai_feedback && <div className="mt-2 text-xs text-gray-800 whitespace-pre-wrap" data-testid="ai-feedback"><strong>AI feedback:</strong>  {result.ai_feedback}</div>}
      <details className="mt-3 text-xs"><summary className="cursor-pointer font-bold">Per-question detail</summary>
        <div className="mt-2 space-y-1.5">
          {result.per_question.map((pq,i) => (
            <div key={i} className={pq.correct===false?'text-red-700':'text-emerald-700'}>
              {pq.correct===false?'✗':'✓'} {pq.prompt.slice(0,100)} {pq.explanation && <em className="text-gray-600">— {pq.explanation}</em>}
            </div>
          ))}
        </div>
      </details>
    </div>
  );
};

const DiscussionHub = ({ hub, onReload }) => {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [filtered, setFiltered] = useState(null);
  const [me, setMe] = useState(null);

  useEffect(() => {
    ax.get('/api/auth/me').then(r => setMe(r.data?.user || r.data)).catch(()=>{});
    ax.post(`/api/learning/rooms/${hub.room.room_id}/mark-read`).catch(()=>{});
  }, [hub.room.room_id]);

  const myRole = (() => {
    if (!me) return 'member';
    const role = (me.role || '').toLowerCase();
    if (role === 'admin' || role === 'owner') return 'admin';
    if (role === 'tutor') return 'tutor';
    return 'student';
  })();

  const send = async (kind='post') => {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await ax.post(`/api/learning/rooms/${hub.room.room_id}/messages`, { body, kind });
      setBody('');
      onReload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not post'); }
    finally { setBusy(false); }
  };
  const search = async () => {
    if (!searchQ.trim()) { setFiltered(null); return; }
    try {
      const r = await ax.get(`/api/learning/rooms/${hub.room.room_id}/messages?q=${encodeURIComponent(searchQ)}`);
      setFiltered(r.data?.messages || []);
    } catch (e) { toast.error('Search failed'); }
  };
  const togglePin = async (msg) => {
    try {
      await ax.patch(`/api/learning/rooms/${hub.room.room_id}/messages/${msg.id}?pinned=${!msg.pinned}`);
      onReload();
    } catch (e) { toast.error('Pin failed'); }
  };
  const delMsg = async (msg) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await ax.patch(`/api/learning/rooms/${hub.room.room_id}/messages/${msg.id}?deleted=true`);
      onReload();
    } catch (e) { toast.error('Delete failed'); }
  };

  const canModerate = myRole === 'admin' || myRole === 'tutor';
  const list = filtered || hub.messages;
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="discussion-hub">
      <h2 className="text-base font-bold text-gray-900 flex items-center gap-2"><MessageCircle className="h-4 w-4"/> {hub.room.title}</h2>
      <p className="text-[11px] text-gray-500 mt-0.5">Members: {hub.room.tutor_emails?.join(', ') || '(no tutors yet)'} · Admin · You{hub.room.parent_email?` · Parent`:''}</p>

      {hub.pinned?.length > 0 && (
        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-1.5" data-testid="hub-pinned">
          <div className="text-[10px] uppercase tracking-wider text-amber-800 font-bold inline-flex items-center gap-1"><Pin className="h-3 w-3"/> Pinned</div>
          {hub.pinned.map(m => (
            <div key={m.id} className="text-xs text-gray-900">
              <RoleBadge role={m.author_role}/> <strong>{m.author_name}</strong>: {m.body}
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 flex gap-2 items-center">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400"/>
          <input value={searchQ} onChange={e=>setSearchQ(e.target.value)} onKeyDown={e=>{if(e.key==='Enter') search();}}
            placeholder="Search messages…" className="w-full text-sm border rounded-full pl-9 pr-3 py-1.5" data-testid="hub-search"/>
        </div>
        <button onClick={search} className="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-full font-bold uppercase tracking-wider">Search</button>
        {filtered && <button onClick={()=>{ setFiltered(null); setSearchQ(''); }} className="text-xs text-gray-500 hover:text-gray-700">Clear</button>}
      </div>

      <div className="mt-3 max-h-[400px] overflow-y-auto space-y-2 pr-1" data-testid="hub-messages">
        {list.length === 0 && <div className="text-xs italic text-gray-400">No messages yet. Say hello to your tutors!</div>}
        {list.map(m => (
          <div key={m.id} className={`border rounded-xl p-3 ${m.kind==='announcement'?'border-amber-300 bg-amber-50':m.kind==='tutor_note'?'border-blue-300 bg-blue-50':'border-gray-200'}`} data-testid={`hub-msg-${m.id}`}>
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold flex items-center gap-2 flex-wrap">
              <RoleBadge role={m.author_role}/> {m.author_name} · {new Date(m.created_at).toLocaleString()} {m.kind!=='post' && <span className="text-amber-700">· {m.kind}</span>} {m.pinned && <Pin className="h-2.5 w-2.5 text-amber-600 inline"/>}
            </div>
            <div className="text-sm text-gray-900 mt-1 whitespace-pre-wrap">{m.body}</div>
            {m.file_url && <a href={m.file_url} target="_blank" rel="noreferrer" className="text-xs text-blue-700 underline inline-flex items-center gap-1 mt-1"><Paperclip className="h-3 w-3"/> {m.file_name || 'Attachment'}</a>}
            {canModerate && (
              <div className="mt-2 flex gap-2 text-[10px]">
                <button onClick={() => togglePin(m)} className="text-amber-700 hover:underline inline-flex items-center gap-0.5" data-testid={`mod-pin-${m.id}`}>{m.pinned ? <><PinOff className="h-2.5 w-2.5"/> Unpin</> : <><Pin className="h-2.5 w-2.5"/> Pin</>}</button>
                <button onClick={() => delMsg(m)} className="text-rose-700 hover:underline">Delete</button>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-3 flex gap-2 items-start">
        <textarea rows={2} value={body} onChange={e=>setBody(e.target.value)} placeholder="Write a message…" className="flex-1 text-sm border rounded-lg p-2" data-testid="hub-compose" />
        <div className="flex flex-col gap-1">
          <button onClick={()=>send('post')} disabled={busy||!body.trim()} className="bg-red-700 hover:bg-red-800 disabled:opacity-40 text-white px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1" data-testid="hub-send"><Send className="h-3 w-3"/> Send</button>
          {canModerate && (
            <>
              <button onClick={()=>send('announcement')} disabled={busy||!body.trim()} className="bg-amber-600 hover:bg-amber-700 disabled:opacity-40 text-white px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider" data-testid="hub-announce">Announce</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const RoleBadge = ({ role }) => {
  const map = {
    admin: 'bg-rose-700 text-white',
    owner: 'bg-rose-700 text-white',
    tutor: 'bg-blue-700 text-white',
    student: 'bg-emerald-700 text-white',
    parent: 'bg-purple-700 text-white',
  };
  return <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${map[role] || 'bg-gray-700 text-white'}`}>{(role || 'member').toUpperCase()}</span>;
};

const ProgressDashboard = ({ onOpenTopic }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    ax.get('/api/me/student/learning/progress')
      .then(r => setData(r.data))
      .catch(e => toast.error('Could not load progress'))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-red-700"/></div>;
  if (!data) return null;
  const { readiness, totals, weakest_topics, quiz_history, mock_history, improvement_graph, recommended_next_topic } = data;
  return (
    <div className="space-y-4" data-testid="progress-dashboard">
      <div className="bg-gradient-to-br from-black to-gray-800 text-white rounded-2xl p-5">
        <div className="text-[10px] uppercase tracking-[3px] text-[#D3D86F] font-bold flex items-center gap-1"><Target className="h-3 w-3"/> Selective Entry Readiness</div>
        <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <BigStat label="Overall" value={`${readiness.overall}%`} accent={readiness.overall >= 80}/>
          <BigStat label="Maths" value={`${readiness.mathematics}%`} accent={readiness.mathematics >= 80}/>
          <BigStat label="English" value={`${readiness.english}%`} accent={readiness.english >= 80}/>
          <BigStat label="Predicted Score" value={`${readiness.predicted_selective_entry}%`} accent={readiness.predicted_selective_entry >= 75}/>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MiniStat label="Topics Completed" value={`${totals.topics_completed}/${totals.topics_total}`}/>
        <MiniStat label="Mastered" value={totals.topics_mastered} icon={<Star className="h-3 w-3 text-amber-500"/>}/>
        <MiniStat label="Study Hours" value={`${totals.study_hours}h`}/>
        <MiniStat label="Streak" value={`${totals.streak_days}d`} icon={<Flame className="h-3 w-3 text-rose-500"/>}/>
      </div>

      {recommended_next_topic && (
        <div className="bg-white border border-gray-200 rounded-2xl p-4" data-testid="recommended-next">
          <div className="text-[10px] uppercase tracking-[2px] text-red-700 font-bold flex items-center gap-1"><Sparkles className="h-3 w-3"/> AI Recommended Next Topic</div>
          <button onClick={() => onOpenTopic(recommended_next_topic.slug)} className="mt-2 text-left w-full hover:bg-red-50/40 rounded-lg p-2 transition">
            <div className="text-sm font-bold text-gray-900">{recommended_next_topic.slug.replace(/-/g, ' ')}</div>
            <div className="text-xs text-gray-600">{recommended_next_topic.reason}</div>
          </button>
        </div>
      )}

      {improvement_graph?.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-2xl p-4" data-testid="improvement-graph">
          <div className="text-sm font-bold text-gray-900 flex items-center gap-2 mb-3"><TrendingUp className="h-4 w-4 text-emerald-600"/> Improvement (last weeks)</div>
          <div className="flex items-end gap-2 h-32">
            {improvement_graph.map(g => (
              <div key={g.week} className="flex-1 flex flex-col items-center gap-1">
                <div className="text-[9px] font-bold text-gray-600">{g.avg_score}%</div>
                <div className="w-full bg-emerald-100 rounded-t" style={{height: `${Math.max(8, g.avg_score)}%`}}>
                  <div className="w-full bg-emerald-500 rounded-t" style={{height: '100%'}}/>
                </div>
                <div className="text-[9px] text-gray-500">{g.week.split('-')[1]}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {weakest_topics?.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-2xl p-4" data-testid="weakest-topics">
          <div className="text-sm font-bold text-gray-900 mb-2">Weakest topics — revise these next</div>
          <div className="space-y-1.5">
            {weakest_topics.slice(0, 5).map(w => (
              <button key={w.topic_slug} onClick={() => onOpenTopic(w.topic_slug)} className="w-full text-left flex items-center justify-between border border-gray-200 rounded-lg px-3 py-2 hover:border-red-300 hover:bg-red-50/40 transition">
                <div>
                  <div className="text-sm font-bold text-gray-900">{w.title || w.topic_slug}</div>
                  <div className="text-[10px] uppercase tracking-wider text-gray-500">{w.track_slug} · {w.difficulty || 'Medium'}</div>
                </div>
                <div className="text-xs text-rose-700 font-bold">{w.best_score_pct || 0}%</div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <HistoryList title="Quiz history" rows={quiz_history?.slice(0, 8) || []}/>
        <HistoryList title="Mock history" rows={mock_history?.slice(0, 8) || []}/>
      </div>
    </div>
  );
};

const BigStat = ({ label, value, accent }) => (
  <div className={`rounded-xl p-3 ${accent ? 'bg-[#D3D86F] text-black' : 'bg-white/10'}`}>
    <div className={`text-[10px] uppercase tracking-wider font-bold ${accent ? 'text-black/70' : 'text-white/70'}`}>{label}</div>
    <div className="text-2xl font-bold mt-0.5">{value}</div>
  </div>
);
const MiniStat = ({ label, value, icon }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-3">
    <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold flex items-center gap-1">{icon}{label}</div>
    <div className="text-base font-bold text-gray-900 mt-0.5">{value}</div>
  </div>
);
const HistoryList = ({ title, rows }) => (
  <div className="bg-white border border-gray-200 rounded-2xl p-4">
    <div className="text-sm font-bold text-gray-900 mb-2">{title}</div>
    {rows.length === 0 && <div className="text-xs italic text-gray-400">No attempts yet.</div>}
    <div className="space-y-1.5">
      {rows.map(a => (
        <div key={a.id} className="flex items-center justify-between text-xs">
          <div className="truncate flex-1 pr-2">{a.reference_slug}</div>
          <div className={`font-bold ${a.score_pct >= 80 ? 'text-emerald-700' : a.score_pct >= 60 ? 'text-amber-700' : 'text-rose-700'}`}>{a.score_pct ?? '-'}%</div>
        </div>
      ))}
    </div>
  </div>
);

const ResourcesLibrary = ({ resources }) => {
  const [category, setCategory] = useState('all');
  const [open, setOpen] = useState(null);
  const cats = ['all', ...new Set(resources.map(r => r.category))];
  const filtered = category === 'all' ? resources : resources.filter(r => r.category === category);
  const openResource = async (slug) => {
    try {
      const r = await ax.get(`/api/learning/resources/${slug}`);
      setOpen(r.data);
    } catch (e) { toast.error('Resource unavailable'); }
  };
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="resources-library">
      <h2 className="text-base font-bold text-gray-900 flex items-center gap-2"><BookOpen className="h-4 w-4"/> Resource Library</h2>
      <p className="text-[11px] text-gray-500 mt-0.5">Formula sheets, vocabulary, grammar guides, worked-example booklets, revision notes, worksheets, homework + challenge sets.</p>
      <div className="mt-3 flex flex-wrap gap-1.5" data-testid="resources-filter">
        {cats.map(c => (
          <button key={c} onClick={()=>setCategory(c)} className={`text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full ${category===c?'bg-red-700 text-white':'bg-gray-100 hover:bg-gray-200'}`} data-testid={`res-cat-${c}`}>{c.replace(/-/g,' ')}</button>
        ))}
      </div>
      <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
        {filtered.map(r => (
          <button key={r.slug} onClick={()=>openResource(r.slug)} className="text-left border border-gray-200 rounded-xl p-3 hover:border-red-300 hover:bg-red-50/40 transition" data-testid={`resource-${r.slug}`}>
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">{r.category.replace(/-/g,' ')} · {r.track}</div>
            <div className="text-sm font-bold text-gray-900 mt-0.5">{r.title}</div>
            <div className="text-xs text-gray-600 mt-1 line-clamp-2">{r.summary}</div>
          </button>
        ))}
        {filtered.length === 0 && <div className="text-xs italic text-gray-400 col-span-3">No resources in this category.</div>}
      </div>
      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={()=>setOpen(null)}>
          <div className="bg-white rounded-2xl p-5 max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={e=>e.stopPropagation()} data-testid="resource-modal">
            <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">{open.category} · {open.track}</div>
            <h3 className="text-lg font-bold text-gray-900 mt-1">{open.title}</h3>
            <p className="text-xs text-gray-500 mt-1">{open.summary}</p>
            <div className="mt-3 whitespace-pre-wrap text-sm text-gray-800 leading-relaxed">{open.content_text}</div>
            <div className="mt-4 flex justify-between items-center">
              <span className="text-[10px] uppercase tracking-wider text-gray-400">{open.downloads || 0} opens</span>
              <button onClick={()=>setOpen(null)} className="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-full">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LearningCentrePage;
